import { OnOff } from '../../../../generated/graphql'
import type {
  BemLimitValuesDataInput,
  BuildingEnergyModelFragment,
  CreateBuildingEnergyModelInput,
  UpdateBuildingEnergyModelInput,
} from '../../../../generated/graphql'
import { parseDecimal } from '../../../../util/parsers'

export type CalibrationFormValues = {
  description: string
  area: string
  heatingCorrection: string
  measuredStart: Date
  outdoorTempHsId: string | undefined
  heatingHsId: string | undefined
  electricityHsId: string | undefined
  coolingHsId: string | undefined
  weeklyProfile: boolean[]
  monthlyProfile: boolean[]
  winterTempStart: string
  winterTempEnd: string
  summerTempStart: string
  summerTempEnd: string
  zeroWeatherTempStart: string
  zeroWeatherTempEnd: string
  morningStartHour: string
  morningEndHour: string
  dayStartHour: string
  dayEndHour: string
  eveningStartHour: string
  eveningEndHour: string
  nightStartHour: string
  nightEndHour: string
}

export const mapCalibrationFormValuesToInput = (
  values: CalibrationFormValues,
):
  | Partial<CreateBuildingEnergyModelInput>
  | Partial<UpdateBuildingEnergyModelInput> => {
  const limitValues: BemLimitValuesDataInput = {
    winterTempStart: parseDecimal(values.winterTempStart),
    winterTempEnd: parseDecimal(values.winterTempEnd),
    zeroWeatherTempStart: parseDecimal(values.zeroWeatherTempStart),
    zeroWeatherTempEnd: parseDecimal(values.zeroWeatherTempEnd),
    summerTempStart: parseDecimal(values.summerTempStart),
    summerTempEnd: parseDecimal(values.summerTempEnd),
    morningStartHour: parseInt(values.morningStartHour, 10),
    morningEndHour: parseInt(values.morningEndHour, 10),
    dayStartHour: parseInt(values.dayStartHour, 10),
    dayEndHour: parseInt(values.dayEndHour, 10),
    eveningStartHour: parseInt(values.eveningStartHour, 10),
    eveningEndHour: parseInt(values.eveningEndHour, 10),
    nightStartHour: parseInt(values.nightStartHour, 10),
    nightEndHour: parseInt(values.nightEndHour, 10),
  }

  return {
    calibrationDescription: values.description,
    calibrationArea: parseDecimal(values.area),
    calibrationCorrection: parseDecimal(values.heatingCorrection),
    calibrationStart: values.measuredStart.toISOString(),
    calOutdoorHourSeriesId: values.outdoorTempHsId,
    calHeatingHourSeriesId: values.heatingHsId,
    calElectricityHourSeriesId: values.electricityHsId,
    calCoolingHourSeriesId: values.coolingHsId,
    calibrationWeeklyProfile: values.weeklyProfile.map((v) =>
      v ? OnOff.On : OnOff.Off,
    ),
    calibrationMonthlyProfile: values.monthlyProfile.map((v) =>
      v ? OnOff.On : OnOff.Off,
    ),
    calibrationLimitValues: limitValues,
  }
}

export const mapInputToCalibrationFormValues = ({
  calibrationDescription,
  calibrationArea,
  calibrationCorrection,
  calibrationStart,
  calOutdoorHourSeries,
  calHeatingHourSeries,
  calElectricityHourSeries,
  calCoolingHourSeries,
  calibrationWeeklyProfile,
  calibrationMonthlyProfile,
  calibrationLimitValues,
}: BuildingEnergyModelFragment): Partial<CalibrationFormValues> => ({
  description: calibrationDescription
    ? String(calibrationDescription)
    : undefined,
  area: calibrationArea ? String(calibrationArea) : undefined,
  heatingCorrection: calibrationCorrection
    ? String(calibrationCorrection)
    : undefined,
  measuredStart: calibrationStart ? new Date(calibrationStart) : undefined,
  outdoorTempHsId: calOutdoorHourSeries?.id,
  heatingHsId: calHeatingHourSeries?.id,
  electricityHsId: calElectricityHourSeries?.id,
  coolingHsId: calCoolingHourSeries?.id,
  weeklyProfile: calibrationWeeklyProfile
    ? calibrationWeeklyProfile.map((v) => v === OnOff.On)
    : undefined,
  monthlyProfile: calibrationMonthlyProfile
    ? calibrationMonthlyProfile.map((v) => v === OnOff.On)
    : undefined,
  winterTempStart: String(calibrationLimitValues?.winterTempStart),
  winterTempEnd: String(calibrationLimitValues?.winterTempEnd),
  summerTempStart: String(calibrationLimitValues?.summerTempStart),
  summerTempEnd: String(calibrationLimitValues?.summerTempEnd),
  zeroWeatherTempStart: String(calibrationLimitValues?.zeroWeatherTempStart),
  zeroWeatherTempEnd: String(calibrationLimitValues?.zeroWeatherTempEnd),
  morningStartHour: String(calibrationLimitValues?.morningStartHour),
  morningEndHour: String(calibrationLimitValues?.morningEndHour),
  dayStartHour: String(calibrationLimitValues?.dayStartHour),
  dayEndHour: String(calibrationLimitValues?.dayEndHour),
  eveningStartHour: String(calibrationLimitValues?.eveningStartHour),
  eveningEndHour: String(calibrationLimitValues?.eveningEndHour),
  nightStartHour: String(calibrationLimitValues?.nightStartHour),
  nightEndHour: String(calibrationLimitValues?.nightEndHour),
})

export const defaultCalibrationValues: Partial<CalibrationFormValues> = {
  description: '',
  area: '',
  heatingCorrection: '1',
  measuredStart: new Date(),
  outdoorTempHsId: undefined,
  heatingHsId: undefined,
  electricityHsId: undefined,
  coolingHsId: undefined,
  weeklyProfile: Array(7).fill(true),
  monthlyProfile: Array(12).fill(true),
  winterTempStart: '-16',
  winterTempEnd: '-14',
  summerTempStart: '16',
  summerTempEnd: '20',
  zeroWeatherTempStart: '-2',
  zeroWeatherTempEnd: '2',
  morningStartHour: '7',
  morningEndHour: '8',
  dayStartHour: '12',
  dayEndHour: '14',
  eveningStartHour: '19',
  eveningEndHour: '21',
  nightStartHour: '2',
  nightEndHour: '4',
}
